import React from "react"

// See https://www.d3indepth.com/shapes/#arc-generator for docs
import { arc as arcBuilder } from "d3-shape"
import { d2r } from "../../utils/trig"
import { Theme, useTheme } from "@mui/material"
import { isValidColor } from "../../utils/color"
import useId from "react-use-uuid"

type TSectorColor = string | number | string[]

interface ISectorProps {
  /**
   * Additional svg styles, to be used when pathOnly = false
   * @default {}
   */
  containerStyles?: React.CSSProperties
  /**
   * Start angle, in degrees. This must be < endAngle. (can be negative)
   */
  startAngle: number
  /**
   * End angle, in degrees.
   */
  endAngle: number
  /**
   * The arc's inner radius, which can be used to give the shape a hole at the origin.
   */
  innerRadius?: number
  /**
   * Path fill colour. Passing a number will use that index from the Gamma UI series palette.
   */
  color: TSectorColor
  /**
   * Additional path props. (for more advanced styling). Note, this is applied to a <circle> node which is clipped into shape.
   */
  pathProps?: React.SVGProps<SVGCircleElement>
  /**
   * The arc's outer radius. (the actual radius)
   */
  outerRadius: number
  /**
   * Return only the path node. Turning off will return an SVG wrapper.
   * @default true
   */
  pathOnly?: boolean
  /**
   * An opacity mask from the inside to the outside.
   * @default [1,1]
   */
  opacityGradient?: number[]
}

const getColorFromProp = (
  color: string | number,
  defaultColor: string,
  theme: Theme
) => {
  let activeColor = defaultColor
  if (typeof color === "number") {
    activeColor = theme.series[color % theme.series.length]
  } else if (
    (color && isValidColor(color)) ||
    (typeof color === "string" && color.indexOf("url(") !== -1)
  ) {
    activeColor = color
  } else if (
    color &&
    /primary|secondary|error|warning|info|spice|success/.test(color) &&
    color in theme.palette
  ) {
    activeColor = theme.palette[color].light
  }
  return activeColor
}

export const Sector = ({
  startAngle,
  endAngle,
  innerRadius = 0,
  outerRadius,
  pathOnly = true,
  color,
  pathProps = {},
  containerStyles = {},
  opacityGradient = []
}: ISectorProps) => {
  const theme = useTheme()
  const id = useId()
  const arcPath = arcBuilder()({
    innerRadius,
    outerRadius,
    startAngle: d2r(startAngle),
    endAngle: d2r(endAngle)
  })

  const activeColor = Array.isArray(color)
    ? color.map(c => getColorFromProp(c, theme.palette.primary.light, theme))
    : getColorFromProp(color, theme.palette.primary.light, theme)

  if (!arcPath) {
    return <p>Arc construction error</p>
  }

  const width = outerRadius * 2

  const radialGradientId = `sector-radial-grad--${id}`
  const clipPathId = `sector-clip--${id}`
  const opacityMaskId = `opacity-mask--${id}`
  const opacityMaskGradId = `opacity-mask-grad--${id}`

  const fillProps = Array.isArray(activeColor)
    ? { fill: `url(#${radialGradientId})`, clipPath: `url(#${clipPathId})` }
    : { fill: activeColor, clipPath: `url(#${clipPathId})` }

  // the base circle is clipped into shape with this path
  // this is so we can position the radial gradient in the center of the circle
  const arcPathNode = <path fill="white" d={arcPath} />

  const clipPath = <clipPath id={clipPathId}>{arcPathNode}</clipPath>

  const opacityMask = (
    <mask id={opacityMaskId}>
      <circle r={outerRadius} fill={`url(#${opacityMaskGradId})`} />
    </mask>
  )
  const opacityMaskGrad = (
    <radialGradient id={opacityMaskGradId} cx="50%" cy="50%" r="50%">
      {opacityGradient.map((v, i) => (
        <stop
          key={`stop-${id}__${i}`}
          offset={i / (opacityGradient.length - 1)}
          stopOpacity={v}
          stopColor={"white"}
        />
      ))}
    </radialGradient>
  )

  const defs = Array.isArray(activeColor) ? (
    <defs>
      <radialGradient id={radialGradientId} cx="50%" cy="50%" r="50%">
        {activeColor.map((c, i) => (
          <stop
            key={`stop-${id}__${i}`}
            offset={i / (activeColor.length - 1)}
            stopOpacity={1}
            stopColor={c}
          />
        ))}
      </radialGradient>
      {clipPath}
      {opacityMaskGrad}
      {opacityMask}
    </defs>
  ) : (
    <defs>
      {clipPath}
      {opacityMaskGrad}
      {opacityMask}
    </defs>
  )

  const baseCircleToBeClipped = (
    <circle
      r={outerRadius}
      {...fillProps}
      {...pathProps}
      mask={opacityGradient.length ? `url(#${opacityMaskId})` : "none"}
    />
  )

  return (
    <>
      {pathOnly ? (
        <g>
          {baseCircleToBeClipped}
          {defs}
        </g>
      ) : (
        <svg
          height={width}
          width={width}
          viewBox={`-${outerRadius} -${outerRadius} ${width} ${width}`}
          style={containerStyles}
        >
          {baseCircleToBeClipped}
          {defs}
        </svg>
      )}
    </>
  )
}

export default Sector
